import { AmbientWorldEventKind, type AmbientWorldEventKind as AmbientWorldEventKindType } from './fishing-world-catalog'
import {
  FishingWorldSchedulerSuppressionReason,
  type FishingWorldSchedulerInput,
  type FishingWorldSchedulerResult,
} from './fishing-world-scheduler'
import { createMicroLifePresentation } from './fishing-world-anchors'

const legacyEventKinds = [
  AmbientWorldEventKind.Birds,
  AmbientWorldEventKind.FishJump,
  AmbientWorldEventKind.Badger,
  AmbientWorldEventKind.Nessie,
] as const satisfies readonly AmbientWorldEventKindType[]

type LegacyAmbientEventKind = typeof legacyEventKinds[number]

/** The former renderer cadence, retained as a pure typed producer. */
const legacyCadence = {
  [AmbientWorldEventKind.Birds]: { first: [800, 2_400], recurring: [7_000, 15_000] },
  [AmbientWorldEventKind.FishJump]: { first: [40_000, 70_000], recurring: [40_000, 70_000] },
  [AmbientWorldEventKind.Badger]: { first: [180_000, 420_000], recurring: [180_000, 420_000] },
  [AmbientWorldEventKind.Nessie]: { first: [360_000, 720_000], recurring: [360_000, 720_000] },
} as const satisfies Record<LegacyAmbientEventKind, Readonly<{
  first: readonly [number, number]
  recurring: readonly [number, number]
}>>

function delayInRange(random: FishingWorldSchedulerInput['random'], range: readonly [number, number]) {
  return range[0] + Math.floor(random() * (range[1] - range[0] + 1))
}

/**
 * Reconstructed pre-shared-scheduler behavior. It intentionally has no shared
 * composition, exclusivity, or cooldown constraints, while retaining cleanup
 * and the current visibility/reduced-motion lifecycle protections.
 */
export function advanceFishingWorldLegacyAmbient(input: FishingWorldSchedulerInput): FishingWorldSchedulerResult {
  const nowMs = input.clock()
  if (!input.lifecycle.visible) {
    return {
      state: { ...input.state, active: [], deferred: true },
      decision: {
        type: 'suppressed',
        reason: FishingWorldSchedulerSuppressionReason.DocumentHidden,
        blockedReasons: [FishingWorldSchedulerSuppressionReason.DocumentHidden],
      },
    }
  }

  const active = input.state.active
    .filter((event) => event.expiresAtMs > nowMs)
    .filter((event) => !input.lifecycle.reducedMotion || input.catalog[event.kind].reducedMotion === 'allow')
  const nextEligibleAtMs = { ...input.state.nextEligibleAtMs }
  const candidates: LegacyAmbientEventKind[] = []

  if (input.state.deferred) {
    for (const kind of legacyEventKinds) {
      const policy = input.catalog[kind]
      if (policy.enabled && (!input.lifecycle.reducedMotion || policy.reducedMotion === 'allow')) {
        nextEligibleAtMs[kind] = nowMs + delayInRange(input.random, legacyCadence[kind].recurring)
      }
    }
    return {
      state: { ...input.state, active, nextEligibleAtMs, deferred: false },
      decision: {
        type: 'suppressed',
        reason: FishingWorldSchedulerSuppressionReason.DeferredRescheduled,
        blockedReasons: [FishingWorldSchedulerSuppressionReason.DeferredRescheduled],
      },
    }
  }

  for (const kind of legacyEventKinds) {
    const policy = input.catalog[kind]
    if (!policy.enabled || (input.lifecycle.reducedMotion && policy.reducedMotion === 'suppress')) continue
    const scheduledAtMs = nextEligibleAtMs[kind]
    if (scheduledAtMs === undefined) {
      nextEligibleAtMs[kind] = nowMs + delayInRange(input.random, legacyCadence[kind].first)
    } else if (scheduledAtMs <= nowMs && !active.some((event) => event.kind === kind)) {
      candidates.push(kind)
    }
  }

  const kind = candidates[0]
  if (kind !== undefined) {
    const policy = input.catalog[kind]
    nextEligibleAtMs[kind] = nowMs + delayInRange(input.random, legacyCadence[kind].recurring)
    const id = `legacy:${kind}:${nowMs}`
    const placement = createMicroLifePresentation(kind, id)
    if (placement === null || placement.id !== policy.placement) {
      return {
        state: { ...input.state, active, nextEligibleAtMs, deferred: false },
        decision: {
          type: 'suppressed', reason: FishingWorldSchedulerSuppressionReason.InvalidPlacement,
          blockedReasons: [FishingWorldSchedulerSuppressionReason.InvalidPlacement],
        },
      }
    }
    const event = {
      id,
      kind,
      startedAtMs: nowMs,
      expiresAtMs: nowMs + policy.durationMs,
      presentation: {
        placement,
        ...(kind === AmbientWorldEventKind.Badger
          ? { badgerSide: placement.orientation === 'facing-left' ? 'right' as const : 'left' as const }
          : {}),
      },
    }
    return {
      state: { ...input.state, active: [...active, event], nextEligibleAtMs, deferred: false },
      decision: { type: 'emitted', event },
    }
  }

  const reason = input.lifecycle.reducedMotion
    ? FishingWorldSchedulerSuppressionReason.ReducedMotion
    : FishingWorldSchedulerSuppressionReason.NoEnabledEvents
  return {
    state: { ...input.state, active, nextEligibleAtMs, deferred: input.lifecycle.reducedMotion },
    decision: { type: 'suppressed', reason, blockedReasons: [reason] },
  }
}
